import { Component } from '@theme/component';

/**
 * Enhances the contact form with inline field validation, a submit loading
 * state, and visible feedback for server responses.
 *
 * Native constraint validation runs first, so the `submit` listener only runs
 * once every field is valid. The `invalid` listener surfaces the browser's
 * validation message next to the offending field.
 *
 * @extends {Component}
 */
class ContactFormComponent extends Component {
  /** @type {boolean} */
  #submitting = false;

  /** @returns {HTMLFormElement | null} */
  get #form() {
    return this.querySelector('form');
  }

  /** @returns {HTMLButtonElement | null} */
  get #submitButton() {
    return this.#form?.querySelector('button[type="submit"]') ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('invalid', this.#onInvalid, true);
    this.addEventListener('input', this.#onFieldUpdated);
    this.addEventListener('change', this.#onFieldUpdated);
    window.addEventListener('pageshow', this.#onPageShow);

    this.#revealServerMessage();
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('invalid', this.#onInvalid, true);
    this.removeEventListener('input', this.#onFieldUpdated);
    this.removeEventListener('change', this.#onFieldUpdated);
    window.removeEventListener('pageshow', this.#onPageShow);

    super.disconnectedCallback();
  }

  /**
   * Resets the loading state when the page is restored from the back/forward
   * cache so the form remains usable.
   */
  #onPageShow = () => {
    this.#submitting = false;
    this.#setLoading(false);
  };

  updatedCallback() {
    super.updatedCallback();

    this.#submitting = false;
    this.#setLoading(false);
    this.#revealServerMessage();
  }

  /**
   * Shows a loading state while the native submission is in flight.
   * @param {Event} event
   */
  #onSubmit = (event) => {
    if (this.#submitting) {
      event.preventDefault();
      return;
    }

    this.#submitting = true;
    this.#setLoading(true);
  };

  /**
   * Renders the browser's validation message inline next to the invalid field.
   * @param {Event} event
   */
  #onInvalid = (event) => {
    const field = event.target;
    if (!this.#isField(field)) return;

    this.#setFieldError(field, field.validationMessage);
  };

  /**
   * Clears inline feedback as the customer corrects a field.
   * @param {Event} event
   */
  #onFieldUpdated = (event) => {
    const field = event.target;
    if (!this.#isField(field)) return;

    if (field.validity.valid) this.#setFieldError(field, '');
  };

  /**
   * @param {EventTarget | null} node
   * @returns {node is HTMLInputElement | HTMLTextAreaElement}
   */
  #isField(node) {
    return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @param {string} message
   */
  #setFieldError(field, message) {
    const wrapper = field.closest('.contact-form__field');
    const errorElement = wrapper?.querySelector('.contact-form__field-error');
    if (!(errorElement instanceof HTMLElement)) return;

    errorElement.textContent = message;
    errorElement.hidden = message === '';

    if (message) {
      field.setAttribute('aria-invalid', 'true');
    } else {
      field.removeAttribute('aria-invalid');
    }
  }

  /**
   * @param {boolean} loading
   */
  #setLoading(loading) {
    const button = this.#submitButton;
    if (!button) return;

    if (loading) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  /**
   * Focuses and scrolls to the server success/error message after a reload.
   */
  #revealServerMessage() {
    const success = this.querySelector('.contact-form__success');
    const message = success ?? this.querySelector('.contact-form__error');
    if (!(message instanceof HTMLElement)) return;

    // Shopify marks a successful contact submission with `?contact_posted=true`.
    // Remove it so refreshing the page does not show the thank-you message again.
    if (success) this.#clearPostedParam();

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    message.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    message.focus({ preventScroll: true });
  }

  /**
   * Removes the submission marker from the current URL without navigating.
   */
  #clearPostedParam() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('contact_posted')) return;

    url.searchParams.delete('contact_posted');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  }
}

if (!customElements.get('contact-form-component')) {
  customElements.define('contact-form-component', ContactFormComponent);
}
